import { get } from 'lodash';
import {
  CtaProcessingOption,
  type RestExperience,
  type RestModalCard,
  type RestModalForwards,
} from '../contracts';
import {
  RestModalAction,
  type EventSyncObject,
  type SyncConfig,
} from '../events';
import { type FunnelEventQueue } from './funnel-events';
import { type FunnelSurvey } from './survey';

export type ModalRefundSync = SyncConfig & {
  refund?: unknown;
  method_of_cancel?: unknown;
  url?: string;
};

type ModalActionContext = {
  experience: Pick<RestExperience, 'session' | 'version'> &
    Partial<Pick<RestExperience, 'user'>>;
  modal: RestModalCard;
  action: RestModalForwards;
  survey: FunnelSurvey;
  fallbackCtaId?: string;
  message?: string;
};

type ModalActionQueueInput = ModalActionContext &
  (
    | { kind: 'checkout' | 'send_email' | 'accept_offer' | 'nevermind' }
    | { kind: 'cancel'; refundSync?: ModalRefundSync | null }
    | { kind: 'link'; includeOfferAccepts: boolean; isLifecycle?: boolean }
  );

/** Build the selected submission only. The host owns routing, state and dispatch timing. */
export const createModalActionQueue = (
  input: ModalActionQueueInput
): FunnelEventQueue => {
  const { experience, modal, action, survey, fallbackCtaId, message } = input;
  const commonKeys = {
    session: experience.session,
    id: modal.id,
    name: modal.name,
    version: experience.version,
    card_version: modal.version,
    cta_ids: action.cta_details_list?.map((cta) => cta.cta_id) ?? [
      fallbackCtaId,
    ],
    cta_id: action.cta_details_list?.[0]?.cta_id ?? fallbackCtaId,
    app_id: get(experience, 'user.app_id', null),
    user_id: get(experience, 'user.user.id', null),
  };
  const commonSyncKeys = {
    ...commonKeys,
    direction: 'forwards' as const,
    survey_state: survey,
    validation: action.validation,
  };
  const commonSync: EventSyncObject = {
    ...commonSyncKeys,
    action: action.action,
    url: action.url || null,
    form: { ...action.sync, message },
  };

  switch (input.kind) {
    case 'checkout':
      return { syncs: [{ ...commonSync, isCheckoutProcessing: true }] };
    case 'send_email': {
      const answers = {
        ...survey,
        responses: { ...(survey.responses || {}), [modal.name]: message },
      };
      return {
        synchronous: true,
        events: [
          {
            ...commonKeys,
            event: RestModalAction.accepted_modal_offer,
            properties: {
              sync: action.sync,
              message,
              survey_state: answers,
              modal,
            },
          },
        ],
        syncs: [
          {
            ...commonSyncKeys,
            survey_state: answers,
            action: RestModalAction.accept_offer,
            form: { sync: action.sync, message, modal },
          },
          { ...commonSync, survey_state: answers },
        ],
      };
    }
    case 'accept_offer':
      return {
        synchronous: true,
        events: [
          {
            ...commonKeys,
            event: RestModalAction.accepted_modal_offer,
            action: RestModalAction.accepted_modal_offer,
            name: modal.name,
            properties: {
              sync: action.sync,
              message,
              survey_state: survey,
              modal,
            },
          },
        ],
        syncs: [{ ...commonSync, action: RestModalAction.accept_offer }],
      };
    case 'cancel': {
      const mergedSync: ModalRefundSync = input.refundSync
        ? { ...action.sync, ...input.refundSync }
        : action.sync;
      const refund = mergedSync?.refund ?? false;
      const methodOfCancel = mergedSync?.method_of_cancel;
      // Event form retains the whole override; sync form only adds cancellation fields.
      const eventForm = {
        ...mergedSync,
        ...(refund !== undefined && { refund }),
        ...(methodOfCancel !== undefined && {
          method_of_cancel: methodOfCancel,
        }),
      };
      const syncForm = {
        ...commonSync.form,
        ...(refund !== undefined && { refund }),
        ...(methodOfCancel !== undefined && {
          method_of_cancel: methodOfCancel,
        }),
      };
      const event = {
        ...commonKeys,
        event: 'cancel' as const,
        card_version: modal.version,
        properties: {
          survey_state: survey,
          sync: { ...mergedSync, refund, method_of_cancel: methodOfCancel },
          modal,
          refund,
          method_of_cancel: methodOfCancel,
        },
        form: eventForm,
      };
      return {
        synchronous: true,
        events: [event],
        syncs: [
          {
            ...commonSync,
            url: action.url || mergedSync?.url,
            action: RestModalAction.link,
            form: syncForm,
          },
        ],
      };
    }
    case 'link':
      return {
        synchronous: true,
        events: input.includeOfferAccepts
          ? [
              {
                ...commonKeys,
                event: RestModalAction.accepted_modal_offer,
                action: RestModalAction.accepted_modal_offer,
                name: modal.name,
                properties: {
                  sync: action.sync,
                  message,
                  survey_state: survey,
                  modal,
                },
              },
            ]
          : [],
        syncs: [
          ...(input.includeOfferAccepts
            ? [
                {
                  ...commonSyncKeys,
                  action: RestModalAction.accept_offer,
                  form: { sync: action.sync, message, modal },
                },
              ]
            : []),
          {
            ...commonSync,
            url: input.isLifecycle ? '?close_iframe' : action.url,
            lco_redirect_url: action.url,
            action: RestModalAction.link,
            isCheckoutProcessing:
              action.cta_details_list?.[0]?.processing_option ===
              CtaProcessingOption.chargebee_checkout,
          },
        ],
      };
    case 'nevermind':
      return {
        synchronous: true,
        events: [
          {
            ...commonKeys,
            event: 'nevermind',
            action: RestModalAction.nevermind,
            properties: {
              sync: action.sync,
              message,
              survey_state: survey,
              modal,
            },
          },
        ],
        syncs: [
          {
            ...commonSync,
            action: RestModalAction.link,
            type: action.type,
            direction: 'backwards',
            url: action.url,
            validation: action.validation,
            form: { sync: action.sync, message, modal },
            survey_state: survey,
          },
        ],
      };
  }
};

export const createModalBackwardsQueue = ({
  experience,
  modal,
  action,
  survey,
}: Omit<
  ModalActionContext,
  'message' | 'fallbackCtaId'
>): FunnelEventQueue => ({
  synchronous: true,
  syncs: [
    {
      ...action,
      direction: 'backwards',
      id: modal.id,
      name: modal.name,
      session: experience.session,
      survey_state: survey,
      validation: action.validation,
      form: action.sync,
      app_id: get(experience, 'user.app_id', null),
      version: experience.version,
    },
  ],
});
